import { useMemo } from 'react'

import { ModalContext } from '@domain-group/suit-theme'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'
import type { PageContextPayload } from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditServiceCategoryModal from '@modals/CreateOrEditServiceCategoryModal'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrEditServiceCategoryModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditServiceCategoryModal: {
      closeModal: () => setState({ showCreateOrEditServiceCategoryModal: false }),
      createServiceCategory: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateServiceCategory: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrEditServiceCategoryModal,
  } = state

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    pageFilters,
  } = useFilters()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      showCreateOrEditServiceCategoryModal: (payload: object) => {
        setModalData('CreateOrEditServiceCategoryModal', payload)
        setState({ showCreateOrEditServiceCategoryModal: true })
      },
      ...filterCallbacks,
    },
    defaultFilters,
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <CreateOrEditServiceCategoryModal
          callbacks={callbacks('CreateOrEditServiceCategoryModal', setState)}
          showModal={showCreateOrEditServiceCategoryModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
